import React, { useState } from 'react';
import { CheckCircle, Circle, AlertCircle, Edit3, Trash2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Habit } from '../../types';
import { useHabitStore } from '../../store/habitStore';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { validateHabitCompletionForToday, isHabitScheduledForToday } from '../../utils/habitValidation';
import { resolveHabitIcon } from '../../utils/iconMapping';

// Icon mapping - same as in CreateHabitForm
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  activity: Icons.Activity,
  book: Icons.Book,
  coffee: Icons.Coffee,
  dumbbell: Icons.Dumbbell,
  heart: Icons.Heart,
  home: Icons.Home,
  moon: Icons.Moon,
  music: Icons.Music,
  run: Icons.Zap,
  smile: Icons.Smile,
  star: Icons.Star,
  sun: Icons.Sun,
  target: Icons.Target,
  tree: Icons.TreePine,
  droplet: Icons.Droplet,
  flame: Icons.Flame,
  apple: Icons.Apple,
  bike: Icons.Bike,
  camera: Icons.Camera,
  clock: Icons.Clock,
  eye: Icons.Eye,
  gift: Icons.Gift,
  headphones: Icons.Headphones,
  leaf: Icons.Leaf,
  lightbulb: Icons.Lightbulb,
  map: Icons.Map,
  palette: Icons.Palette,
  phone: Icons.Phone,
  shield: Icons.Shield,
  umbrella: Icons.Umbrella,
  circle: Icons.Circle,
  square: Icons.Square,
  triangle: Icons.Triangle,
  diamond: Icons.Diamond,
  hexagon: Icons.Hexagon,
  octagon: Icons.Octagon,
  // Add more mappings as needed
  water: Icons.Droplet, // alias for droplet
  zap: Icons.Zap,
};

interface HabitCardProps {
  habit: Habit;
  onEdit?: (habit: Habit) => void;
  onDelete?: (habitId: string) => void;
  onToggleCompletion?: (habitId: string, date?: string) => void;
  onClick?: (habit: Habit) => void;
}

// Helper function to create mobile-friendly schedule message
const getScheduleMessage = (message: string, isMobile: boolean = false): string => {
  if (!message.includes('scheduled for')) return message;
  
  // Extract days from the message
  const daysMatch = message.match(/scheduled for (.+)$/);
  if (!daysMatch) return message;
  
  const daysText = daysMatch[1];
  
  if (isMobile) {
    // For mobile, show abbreviated version
    const shortDays = daysText
      .replace(/Monday/g, 'Mon')
      .replace(/Tuesday/g, 'Tue')
      .replace(/Wednesday/g, 'Wed')
      .replace(/Thursday/g, 'Thu')
      .replace(/Friday/g, 'Fri')
      .replace(/Saturday/g, 'Sat')
      .replace(/Sunday/g, 'Sun');
    return `Only on ${shortDays}`;
  }
  
  return `Only scheduled for ${daysText}`;
};

export const HabitCard: React.FC<HabitCardProps> = ({
  habit,
  onEdit,
  onDelete,
  onToggleCompletion,
  onClick,
}) => {
  const { toggleHabitCompletion } = useHabitStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  
  // Use server-calculated streak data from the habit object instead of local calculation
  const streakStats = {
    currentStreak: habit.currentStreak,
    longestStreak: habit.longestStreak,
    completionRate: habit.completionRate,
    totalCompletions: 0, // This isn't provided by the API response, but not used in this component
  };
  
  // Calculate if habit is completed today - prioritize server data over local store
  // Server data is the source of truth, local store is only for optimistic updates
  // const todaysCompletions = getTodaysCompletions();
  const isCompletedToday = habit.isCompletedToday;

  // Check if habit is scheduled for today
  const isScheduledToday = isHabitScheduledForToday(habit);
  const validationResult = validateHabitCompletionForToday(habit);

  const handleToggleCompletion = async () => {
    // Validate before allowing completion
    if (!validationResult.isValid) {
      // Show validation message (you might want to use a toast notification here)
      alert(validationResult.message);
      return;
    }

    // Prevent multiple simultaneous completion attempts
    if (isCompleting) return;

    setIsCompleting(true);

    try {
      if (onToggleCompletion) {
        // Use the passed handler which will refresh dashboard stats
        await onToggleCompletion(habit.id);
      } else {
        // Fallback to local store only
        await toggleHabitCompletion(habit.id);
      }
    } catch (error) {
      console.error('Failed to toggle habit completion:', error);
      // Could show toast notification here instead of console.error
    } finally {
      setIsCompleting(false);
    }
  };

  const handleEdit = () => {
    onEdit?.(habit);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.(habit.id);
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on buttons or interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="button"]')) {
      return;
    }
    onClick?.(habit);
  };

  // Get the appropriate icon component
  const IconComponent = ICON_MAP[habit.iconName.toLowerCase()] || resolveHabitIcon(habit.iconName);

  return (
    <div 
      className={`habit-card group ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3 lg:gap-4">
        <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
          {/* Habit Icon */}
          <div
            className="w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-white flex-shrink-0"
            style={{ backgroundColor: habit.colorCode }}
          >
            <IconComponent size={20} className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
          </div>

          {/* Habit Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight mb-1 break-words">
              {habit.name}
            </h3>
            {habit.description && (
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2 sm:mb-3 line-clamp-2">
                {habit.description}
              </p>
            )}
            <div className="flex items-center flex-wrap gap-2 sm:gap-3 lg:gap-4 mb-2">
              {/* Current Streak */}
              <div className="flex items-center space-x-1">
                <Icons.Flame size={14} className="text-orange-500 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {streakStats.currentStreak} day{streakStats.currentStreak !== 1 ? 's' : ''}
                </span>
              </div>
              
              {/* Completion Rate */}
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {streakStats.completionRate}% this month
              </div>
            </div>
            
            {/* Schedule validation message */}
            {!isScheduledToday && (
              <div className="flex items-start space-x-1 mt-2">
                <AlertCircle size={14} className="text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col space-y-1">
                  {/* Desktop message */}
                  <span className="hidden sm:block text-xs text-amber-600 dark:text-amber-400 leading-relaxed">
                    {getScheduleMessage(validationResult.message || '', false)}
                  </span>
                  {/* Mobile message */}
                  <span className="block sm:hidden text-xs text-amber-600 dark:text-amber-400 leading-relaxed">
                    {getScheduleMessage(validationResult.message || '', true)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Completion Toggle */}
        <div className="flex items-start pt-0.5 sm:pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleCompletion}
            disabled={!isScheduledToday || isCompleting}
            isLoading={isCompleting}
            className={`p-1.5 sm:p-2 flex-shrink-0 ${
              isCompletedToday
                ? 'text-success-600 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300'
                : isScheduledToday
                ? 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400'
                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            }`}
            title={isScheduledToday ? 'Toggle completion' : validationResult.message}
          >
            {isCompletedToday ? (
              <CheckCircle size={20} className="fill-current sm:w-6 sm:h-6" />
            ) : (
              <Circle size={20} className="sm:w-6 sm:h-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Streak Badge */}
      <div className="mt-2 sm:mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap gap-1">
          <span
            className={`streak-badge text-xs sm:text-sm ${
              streakStats.currentStreak > 0 ? 'streak-active' : 'streak-inactive'
            }`}
          >
            Current: {streakStats.currentStreak} days
          </span>
          <span className="streak-badge bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs sm:text-sm">
            Best: {streakStats.longestStreak} days
          </span>
        </div>

        {/* Action Buttons (always visible on mobile, shown on hover on desktop) */}
        <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            leftIcon={<Edit3 size={12} />}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 sm:p-2 text-xs"
          >
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            leftIcon={<Trash2 size={12} />}
            className="text-danger-500 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 p-1 sm:p-2 text-xs"
          >
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-2 sm:mt-3">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span className="truncate">Progress to {habit.targetStreakLength} days</span>
          <span className="flex-shrink-0 ml-2">
            {Math.min(streakStats.currentStreak, habit.targetStreakLength)}/
            {habit.targetStreakLength}
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
          <div
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(
                (streakStats.currentStreak / habit.targetStreakLength) * 100,
                100
              )}%`,
              backgroundColor: habit.colorCode,
            }}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Habit"
        message={`Are you sure you want to delete "${habit.name}"? This action cannot be undone and will permanently remove all associated data including completion history.`}
        confirmText="Delete Habit"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}; 